/**
 * 合并工具函数
 *
 * 提供分支合并过程中需要的纯函数工具方法
 */
import { AI_PROJECT_INFO_SCHEMA_KEY, CONFLICT_MARKER_REGEX, MergeItemStatus, } from '../constants';
// ============================================================================
// 类型判断工具
// ============================================================================
/**
 * 判断是否为 AI 项目信息
 *
 * @param schemaKey - Schema 标识符
 * @returns 是否为 AI 项目信息
 */
export function isAIProjectInfo(schemaKey) {
    return schemaKey === AI_PROJECT_INFO_SCHEMA_KEY;
}
/**
 * 判断合并项是否可以重新解决冲突
 *
 * @description
 * 只有满足以下条件的项才能重新解决：
 * 1. 不是 AI 项目信息
 * 2. 当前状态为"已解决"
 * 3. 曾经存在冲突
 *
 * @param item - 合并项
 * @returns 是否可以重新解决
 */
export function canReResolve(item) {
    const isAI = isAIProjectInfo(item.schemaKey);
    return !isAI && item.sectionType === 'resolved' && Boolean(item.wasConflict);
}
/**
 * 判断合并项是否只读
 *
 * @description
 * 已解决或已合并的项为只读状态
 *
 * @param item - 合并项
 * @returns 是否只读
 */
export function isReadonly(item) {
    return item.sectionType === 'resolved' || item.sectionType === 'merged';
}
// ============================================================================
// 状态转换工具
// ============================================================================
/**
 * 将 SectionType 转换为 MergeItemStatus
 *
 * @param sectionType - 旧版分区类型
 * @returns 新版合并项状态
 */
export function sectionTypeToStatus(sectionType) {
    const mapping = {
        unresolved: MergeItemStatus.CONFLICT_UNRESOLVED,
        resolved: MergeItemStatus.CONFLICT_RESOLVED,
        merged: MergeItemStatus.AUTO_MERGED,
    };
    return mapping[sectionType];
}
/**
 * 将 MergeItemStatus 转换为 SectionType
 *
 * @param status - 新版合并项状态
 * @returns 旧版分区类型
 */
export function statusToSectionType(status) {
    const mapping = {
        [MergeItemStatus.CONFLICT_UNRESOLVED]: 'unresolved',
        [MergeItemStatus.CONFLICT_RESOLVED]: 'resolved',
        [MergeItemStatus.AUTO_MERGED]: 'merged',
    };
    return mapping[status];
}
// ============================================================================
// 冲突检测工具
// ============================================================================
/**
 * 检查文本中是否包含冲突标记
 *
 * @param content - 要检查的文本内容
 * @returns 是否包含冲突标记
 */
export function hasConflictMarkers(content) {
    const regex = new RegExp(CONFLICT_MARKER_REGEX);
    return regex.test(content);
}
/**
 * 统计文本中的冲突数量
 *
 * @param content - 要检查的文本内容
 * @returns 冲突数量
 */
export function countConflicts(content) {
    const regex = new RegExp(CONFLICT_MARKER_REGEX, 'g');
    const matches = content.match(regex);
    return matches ? matches.length : 0;
}
// ============================================================================
// 数据转换工具
// ============================================================================
/**
 * 将 MergeItemInternal 转换为 SubmitChangeItem
 *
 * @description
 * 用于将前端的合并项数据转换为后端接受的提交格式
 *
 * @param item - 前端合并项
 * @param formatToJSON - YAML 转 JSON 的函数
 * @returns 后端提交格式的数据
 * @throws 如果格式转换失败则抛出异常
 */
export function toSubmitChangeItem(item, formatToJSON) {
    const value = formatToJSON(item.content.after);
    return {
        id: item.id,
        schemaInfo: {
            id: item.schemaKey,
        },
        compareData: value,
    };
}
/**
 * 批量转换合并项为提交格式
 *
 * @param items - 合并项列表
 * @param formatToJSON - YAML 转 JSON 的函数
 * @returns 提交格式的数据列表
 */
export function toSubmitChangeItems(items, formatToJSON) {
    const data = [];
    const errors = [];
    items.forEach((item) => {
        try {
            data.push(toSubmitChangeItem(item, formatToJSON));
        }
        catch (err) {
            errors.push({ item, error: err });
        }
    });
    return { data, errors };
}
// ============================================================================
// 列表操作工具
// ============================================================================
/**
 * 按状态过滤合并项
 *
 * @param items - 合并项列表
 * @param sectionType - 要过滤的状态
 * @returns 过滤后的列表
 */
export function filterByStatus(items, sectionType) {
    return items.filter(item => item.sectionType === sectionType);
}
/**
 * 统计未解决冲突数量
 *
 * @param items - 合并项列表
 * @returns 未解决冲突数量
 */
export function countUnresolved(items) {
    return items.filter(item => item.conflict).length;
}
/**
 * 获取所有已解决的项（包括已解决的冲突和自动合并的项）
 *
 * @param conflicts - 冲突列表
 * @param mergedPatches - 已合并列表
 * @returns 所有已解决的项
 */
export function getAllResolvedItems(conflicts, mergedPatches) {
    const resolvedConflicts = conflicts.filter(item => !item.conflict);
    return [...resolvedConflicts, ...mergedPatches];
}
/**
 * 在列表中查找并更新项
 *
 * @param items - 原列表
 * @param key - 要查找的项的 key
 * @param updater - 更新函数
 * @returns 更新后的新列表
 */
export function updateItemByKey(items, key, updater) {
    const index = items.findIndex(item => item.key === key);
    if (index === -1) {
        return items;
    }
    return [
        ...items.slice(0, index),
        updater(items[index]),
        ...items.slice(index + 1),
    ];
}
/**
 * 从列表中移除项
 *
 * @param items - 原列表
 * @param key - 要移除的项的 key
 * @returns 移除后的新列表
 */
export function removeItemByKey(items, key) {
    return items.filter(item => item.key !== key);
}
// ============================================================================
// 排序工具
// ============================================================================
/**
 * 按 Schema 标题排序合并项
 *
 * @description
 * AI 项目信息始终排在最前面
 *
 * @param items - 合并项列表
 * @returns 排序后的新列表
 */
export function sortBySchemaTitle(items) {
    return [...items].sort((a, b) => {
        // AI 项目信息优先
        if (isAIProjectInfo(a.schemaKey))
            return -1;
        if (isAIProjectInfo(b.schemaKey))
            return 1;
        // 其他按标题字母顺序
        return a.schemaTitle.localeCompare(b.schemaTitle);
    });
}
/**
 * 获取默认选中的合并项
 *
 * @description
 * 优先选择 AI 项目信息，其次选择第一个冲突项，最后选择第一个合并项
 *
 * @param conflicts - 冲突列表
 * @param mergedPatches - 已合并列表
 * @returns 默认选中的项，如果列表为空则返回 null
 */
export function getDefaultSelectedItem(conflicts, mergedPatches) {
    // 优先选择 AI 项目信息
    const aiProjectConflict = conflicts.find(item => isAIProjectInfo(item.schemaKey));
    if (aiProjectConflict) {
        return aiProjectConflict;
    }
    // 其次选择第一个冲突
    if (conflicts.length > 0) {
        return conflicts[0];
    }
    // 最后选择第一个合并项
    if (mergedPatches.length > 0) {
        return mergedPatches[0];
    }
    return null;
}
